import {
  format,
  parse,
  formatRelative,
  formatDistanceToNow,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
} from 'date-fns';
import { th, enUS } from 'date-fns/locale';
import { StoreTimezoneKey } from '@/consts';
import { isClient } from './appUtil';
import { useStoreStore } from '@/stores/store-store';

/**
 * Canonical date-format presets. Use these instead of raw format strings.
 * The preset name describes intent (display purpose); the resolved pattern depends on locale.
 *
 * **Numeric family (for inputs / data transport / dense tables):**
 * - `date`     → date only — `MM/DD/YYYY`
 * - `dateTime` → date + time numeric — `MM/DD/YYYY hh:mm a`
 * - `time`     → time only — `hh:mm a`
 * - `monthDay` → month + day (no year) — `MM/DD`
 * - `short`    → 2-digit year, compact — `MM/DD/YY`
 * - `range`    → alias of `date` for range-picker call sites
 *
 * **Long/semantic family (for read-only display labels — preferred for tables/modals/receipts):**
 * - `long`         → `MMM DD, YYYY` — less ambiguous than numeric
 * - `longDateTime` → `MMM DD, YYYY hh:mm a` — long date + time
 */
export type DateFormatPreset = 'date' | 'dateTime' | 'time' | 'monthDay' | 'long' | 'longDateTime' | 'short' | 'range';

/** Locales that own their own date-format pattern table. */
export type DateFormatLocale = 'en' | 'es' | 'vi' | 'zh';

export interface FormatDateOptions {
  preset?: DateFormatPreset;
  locale?: DateFormatLocale;
  timezone?: string;
}

const PRESET_BY_LOCALE: Record<DateFormatPreset, Record<DateFormatLocale, string>> = {
  date:         { en: 'MM/dd/yyyy',          es: 'dd/MM/yyyy',         vi: 'dd/MM/yyyy',         zh: 'yyyy/MM/dd' },
  dateTime:     { en: 'MM/dd/yyyy hh:mm a',  es: 'dd/MM/yyyy HH:mm',   vi: 'dd/MM/yyyy HH:mm',   zh: 'yyyy/MM/dd HH:mm' },
  time:         { en: 'hh:mm a',             es: 'HH:mm',              vi: 'HH:mm',              zh: 'HH:mm' },
  monthDay:     { en: 'MM/dd',               es: 'dd/MM',              vi: 'dd/MM',              zh: 'MM/dd' },
  long:         { en: 'MMM dd, yyyy',        es: 'dd MMM yyyy',        vi: 'dd MMM yyyy',        zh: 'yyyy MMM dd' },
  longDateTime: { en: 'MMM dd, yyyy hh:mm a', es: 'dd MMM yyyy HH:mm', vi: 'dd MMM yyyy HH:mm',  zh: 'yyyy MMM dd HH:mm' },
  short:        { en: 'MM/dd/yy',            es: 'dd/MM/yy',           vi: 'dd/MM/yy',           zh: 'yy/MM/dd' },
  range:        { en: 'MM/dd/yyyy',          es: 'dd/MM/yyyy',         vi: 'dd/MM/yyyy',         zh: 'yyyy/MM/dd' },
};

// ─── Internal format constants ──────────────────────────────────────────────
// Kept un-exported because they're only consumed by `getDate(Time)AutoFormatBy`,
// `formatTimeWithStoreTimezone`, and `formatDateLegacy` inside this module.

const FORMAT_ISO_DATE = 'yyyy-MM-dd';
const FORMAT_SHORT_DDMMYY = 'dd/MM/yy';
const FORMAT_DDMM_HHMM = 'dd/MM HH:mm';
const FORMAT_DDMMYYYY_HHMM = 'dd/MM/yyyy HH:mm';
const FORMAT_HHMM = 'HH:mm';
const FORMAT_DDMM = 'dd/MM';

// ─── Timezone resolver ──────────────────────────────────────────────────────

/**
 * Resolve the store's timezone. Prefers the Pinia `storeStore` (live state), with
 * `localStorage[StoreTimezoneKey]` as fallback for callers that run before Pinia
 * hydrates (e.g. early axios interceptors, SSR). Final fallback is `'UTC'`.
 */
export const getTimezoneFromStorage = (): string => {
  if (!isClient()) {
    return 'UTC';
  }
  try {
    const tz = useStoreStore().selectedStore?.timezone;
    if (tz) return tz;
  } catch {
    // Pinia not initialized yet — fall through to localStorage.
  }
  return localStorage.getItem(StoreTimezoneKey) || 'UTC';
};

// ─── Time-of-day conversion ─────────────────────────────────────────────────

/** Convert `"HH:mm"` (24h) to `"hh:mm AM/PM"` (12h); preserves null for form fields. */
export const convertTo12h = (time24h: string | null | undefined): string | null => {
  if (!time24h) return null;
  const match = time24h.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return time24h;
  const hour = Number.parseInt(match[1] ?? '0', 10);
  const minute = match[2];
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12.toString().padStart(2, '0')}:${minute} ${period}`;
};

/** Convert `"hh:mm AM/PM"` (12h) to `"HH:mm"` (24h); preserves null for form fields. */
export const convertTo24h = (time12h: string | null | undefined): string | null => {
  if (!time12h) return null;
  const match = time12h.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return time12h;
  let hour = Number.parseInt(match[1] ?? '0', 10);
  const minute = match[2];
  const period = (match[3] ?? '').toUpperCase();
  if (period === 'AM' && hour === 12) hour = 0;
  if (period === 'PM' && hour !== 12) hour += 12;
  return `${hour.toString().padStart(2, '0')}:${minute}`;
};

/** Convert `"HH:mm"` (24h) string to `"hh:mm AM/PM"` (12h). Returns `'-'` for empty input. */
export const formatTo12h = (time24h: string | null | undefined): string => {
  if (!time24h) return '-';
  const match = time24h.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return time24h;
  const hour = Number.parseInt(match[1] ?? '0', 10);
  const minute = match[2];
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12.toString().padStart(2, '0')}:${minute} ${period}`;
};

/** 12h time in store timezone (`getTimezoneFromStorage`); pass `timezone` to override. */
export function formatTo12HourTime(
  isoString: string,
  locale: string = 'en-US',
  timezone?: string,
): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const tz = timezone ?? getTimezoneFromStorage();
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: tz,
    }).format(date);
  } catch {
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: true });
  }
}

/**
 * Time label for one appointment-detail row in the check-in "booking details" popup.
 * A booked detail carries a scheduled window → "h:mm AM - h:mm PM". A walk-in add-turn has NO
 * schedule (scheduledStart/End null) but, once started, has an `actualStartTime` → fall back to
 * the actual service window so a second same-customer tech no longer shows an empty time.
 * Returns '' when nothing usable exists (caller renders its '-' placeholder).
 *
 * NB the params are widened to `string | null | undefined` on purpose: the swagger type
 * `AppointmentDetailsPopulated` declares `scheduled*` as a required `string`, but the check-in
 * dashboard endpoint sends `null` at runtime for a walk-in serve-now (no reservation) AND for a
 * booked turn with `estimatedDurationMinutes = 0` (start set, end computed to null). The `?? ''`
 * guards below MUST stay — do not "simplify" them away trusting the compile-time type.
 */
export function formatAppointmentTimeRange(
  detail: {
    scheduledStartTime?: string | null | undefined;
    scheduledEndTime?: string | null | undefined;
    actualStartTime?: string | null | undefined;
    actualEndTime?: string | null | undefined;
  } | null | undefined,
  locale: string = 'en-US',
  timezone?: string,
): string {
  if (!detail) return '';
  const start = formatTo12HourTime(detail.scheduledStartTime ?? '', locale, timezone);
  const end = formatTo12HourTime(detail.scheduledEndTime ?? '', locale, timezone);
  if (start && end) return `${start} - ${end}`;
  // Booked turn with a start but no computed end (0 estimated duration) → show the start alone
  // rather than a dangling "09:00 AM - ". Intentional single-time display.
  if (start) return start;
  // Unscheduled (walk-in): show the actual window — start alone while still working. The check-in
  // endpoint does NOT surface `actualEndTime` today, so the range branch is forward-compat only
  // (reachable once the API sends an actual end, or when this helper is reused elsewhere).
  const actualStart = formatTo12HourTime(detail.actualStartTime ?? '', locale, timezone);
  const actualEnd = formatTo12HourTime(detail.actualEndTime ?? '', locale, timezone);
  if (actualStart && actualEnd) return `${actualStart} - ${actualEnd}`;
  return actualStart;
}

// ─── String / Date conversion helpers ───────────────────────────────────────

export const removeTime = (datetimeString: string) => {
  return datetimeString ? datetimeString.split(' ')[0] : '';
};

export const convertStringToDate = (
  dateString: string,
  format = 'yyyy-MM-dd HH:mm:ss',
): Date => {
  return parse(dateString, format, new Date());
};

/** Convert `yyyy-MM-dd` → `MM-dd-yyyy`. Returns empty string for invalid/empty input. */
export const formatYmdToMdy = (ymd: string | null | undefined): string => {
  if (!ymd) return '';
  const [yy, mm, dd] = ymd.split('-');
  if (!yy || !mm || !dd) return '';
  return `${mm}-${dd}-${yy}`;
};

/**
 * Convert a `MM/dd/yyyy` q-date value → `yyyy-MM-dd` (the `DateOnly` DTO format).
 * Pure wall-clock reshape — no timezone conversion, so it never drifts a day.
 * Returns empty string for invalid/empty input.
 */
export const formatMdyToYmd = (mdy: string | null | undefined): string => {
  if (!mdy) return '';
  const [mm, dd, yy] = mdy.split('/');
  if (!yy || !mm || !dd) return '';
  return `${yy}-${mm}-${dd}`;
};

/**
 * Format a `{ from, to }` YMD range for UI display.
 * - Both ends present → `MM-dd-yyyy — MM-dd-yyyy`
 * - Partial         → whichever end is set
 * - Empty / null    → fallback
 */
export const formatYmdRangeLabel = (
  range: { from?: string | null; to?: string | null } | null | undefined,
  fallback = '',
): string => {
  const from = formatYmdToMdy(range?.from);
  const to = formatYmdToMdy(range?.to);
  if (from && to) return `${from} — ${to}`;
  if (from || to) return from || to;
  return fallback;
};

// ─── "Now" helpers ──────────────────────────────────────────────────────────

// addDateByDays đã tách sang '@/utils/addDateByDays' để không leak date-fns vào boot chunk.
export const getDateNow = () => new Date();
export const getYearNow = () => new Date().getFullYear();
export const getMonthNow = () => new Date().getMonth();
export const getCurrentTimestamp = () => Date.now();

export const getCurrentDateByFormat = (
  forMatString: string = FORMAT_ISO_DATE,
  timezone?: string,
) => {
  const d = new Date();
  const tz = timezone ?? getTimezoneFromStorage();
  return formatInstantForPattern(d, forMatString, tz) ?? format(d, forMatString);
};

export const getCurrentDateWithStoreTimezone = (timezone?: string): string => {
  const storeTimezone = timezone || getTimezoneFromStorage();
  const now = new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone: storeTimezone,
    }).formatToParts(now);
    const month = parts.find(p => p.type === 'month')?.value ?? '01';
    const day = parts.find(p => p.type === 'day')?.value ?? '01';
    const year = parts.find(p => p.type === 'year')?.value ?? '1970';
    return `${month}-${day}-${year}`;
  } catch (error) {
    console.error('Error getting current date with store timezone:', error);
    return getCurrentDateByFormat(FORMAT_ISO_DATE);
  }
};

// ─── Diff helpers ───────────────────────────────────────────────────────────

export const getDateDiff = (
  dateLeft: Date | number,
  dateRight: Date | number,
) => differenceInDays(dateRight, dateLeft);

export const getDateDiffNow = (dateString: string) => {
  const d = removeTime(dateString);
  const currentDate = removeTime(getCurrentDateByFormat());
  if (!d || !currentDate) return 0;
  return getDateDiff(Date.parse(d), Date.parse(currentDate));
};

/** @param dateLeft the later date @param dateRight the earlier date */
export const getDateDiffMinutes = (
  dateLeft: Date | number,
  dateRight: Date | number,
) => differenceInMinutes(dateRight, dateLeft);

// ─── Internal: Intl-based formatter for known patterns ─────────────────────

const part = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) =>
  parts.find(p => p.type === type)?.value ?? '';

/** Format an instant for known patterns; null → caller falls back to date-fns. */
function formatInstantForPattern(
  date: Date,
  forMatString: string,
  timezone: string,
): string | null {
  if (Number.isNaN(date.getTime())) return null;
  try {
    switch (forMatString) {
      case FORMAT_ISO_DATE:
      case 'yyyy-MM-dd':
        return new Intl.DateTimeFormat('en-CA', {
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(date);
      case FORMAT_HHMM:
        return new Intl.DateTimeFormat('en-GB', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(date);
      case FORMAT_SHORT_DDMMYY: {
        const p = new Intl.DateTimeFormat('en-GB', {
          timeZone: timezone,
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        }).formatToParts(date);
        return `${part(p, 'day')}/${part(p, 'month')}/${part(p, 'year')}`;
      }
      case FORMAT_DDMM: {
        const p = new Intl.DateTimeFormat('en-GB', {
          timeZone: timezone,
          day: '2-digit',
          month: '2-digit',
        }).formatToParts(date);
        return `${part(p, 'day')}/${part(p, 'month')}`;
      }
      case FORMAT_DDMM_HHMM: {
        const p = new Intl.DateTimeFormat('en-GB', {
          timeZone: timezone,
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).formatToParts(date);
        return `${part(p, 'day')}/${part(p, 'month')} ${part(p, 'hour')}:${part(p, 'minute')}`;
      }
      case FORMAT_DDMMYYYY_HHMM: {
        const p = new Intl.DateTimeFormat('en-GB', {
          timeZone: timezone,
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).formatToParts(date);
        return `${part(p, 'day')}/${part(p, 'month')}/${part(p, 'year')} ${part(p, 'hour')}:${part(p, 'minute')}`;
      }
      case 'MM/dd/yyyy': {
        const p = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }).formatToParts(date);
        return `${part(p, 'month')}/${part(p, 'day')}/${part(p, 'year')}`;
      }
      case 'MM/dd/yy': {
        const p = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
        }).formatToParts(date);
        return `${part(p, 'month')}/${part(p, 'day')}/${part(p, 'year')}`;
      }
      case 'MM/dd': {
        const p = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          month: '2-digit',
          day: '2-digit',
        }).formatToParts(date);
        return `${part(p, 'month')}/${part(p, 'day')}`;
      }
      case 'MM/dd/yyyy hh:mm a': {
        const p = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }).formatToParts(date);
        return `${part(p, 'month')}/${part(p, 'day')}/${part(p, 'year')} ${part(p, 'hour')}:${part(p, 'minute')} ${part(p, 'dayPeriod').toUpperCase()}`;
      }
      case 'hh:mm a': {
        const p = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }).formatToParts(date);
        return `${part(p, 'hour')}:${part(p, 'minute')} ${part(p, 'dayPeriod').toUpperCase()}`;
      }
      case 'MMM dd, yyyy': {
        const p = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        }).formatToParts(date);
        return `${part(p, 'month')} ${part(p, 'day')}, ${part(p, 'year')}`;
      }
      case 'MMM dd, yyyy hh:mm a': {
        const p = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          month: 'short',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }).formatToParts(date);
        return `${part(p, 'month')} ${part(p, 'day')}, ${part(p, 'year')} ${part(p, 'hour')}:${part(p, 'minute')} ${part(p, 'dayPeriod').toUpperCase()}`;
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

// ─── Canonical `formatDate` ─────────────────────────────────────────────────

/**
 * Format a date. Two call styles:
 *
 * - **Preset (new, canonical)**: `formatDate('2026-01-05')` → `'01/05/2026'` (default preset `'date'`, locale `'en'`).
 *   Pass options to choose preset/locale/timezone:
 *   `formatDate(dateStr, { preset: 'long', locale: 'vi' })` → `'05 Jan 2026'`.
 *
 * - **Legacy (raw date-fns pattern)**: `formatDate(dateStr, 'dd MMMM yyyy', locale.value)`.
 *   Kept for backwards compat with `useBase.appFormatDate`. Prefer preset form for new code.
 */
export function formatDate(input: string | Date | undefined, opts?: FormatDateOptions): string;
export function formatDate(
  input: string | undefined,
  format: string,
  locale: string | unknown,
  timezone?: string,
): string | undefined;
export function formatDate(
  input: string | Date | undefined,
  optsOrFormat?: FormatDateOptions | string,
  legacyLocale?: string | unknown,
  legacyTimezone?: string,
): string | undefined {
  if (typeof optsOrFormat === 'string') {
    return formatDateLegacy(
      typeof input === 'string' ? input : input?.toISOString(),
      optsOrFormat,
      legacyLocale,
      legacyTimezone,
    );
  }
  if (!input) return '';
  const opts = optsOrFormat ?? {};
  const preset = opts.preset ?? 'date';
  const locale: DateFormatLocale = opts.locale ?? 'en';
  const pattern = PRESET_BY_LOCALE[preset]?.[locale] ?? PRESET_BY_LOCALE.date.en;
  // Pure date input (`YYYY-MM-DD` or `MM/DD/YYYY`) is a wall-clock day — format
  // without any timezone conversion to avoid drifting one day when the store tz
  // disagrees with the implicit parse tz (UTC for ISO, local for slash form).
  if (typeof input === 'string') {
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
    const us = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(input);
    if (iso || us) {
      const [y, m, d] = iso
        ? [Number(iso[1]), Number(iso[2]), Number(iso[3])]
        : [Number(us![3]), Number(us![1]), Number(us![2])];
      if (y && m && d) {
        const anchored = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
        return formatInstantForPattern(anchored, pattern, 'UTC')
          ?? format(anchored, pattern, { locale: enUS });
      }
      return '';
    }
  }
  const timezone = opts.timezone ?? getTimezoneFromStorage();
  const dateStr = typeof input === 'string' ? input : input.toISOString();
  return formatDateLegacy(dateStr, pattern, locale, timezone) ?? '';
}

function formatDateLegacy(
  dateString: string | undefined,
  forMatString: string,
  locale: string | unknown,
  timezone?: string,
) {
  if (!dateString) return undefined;
  const tz = timezone ?? getTimezoneFromStorage();
  let date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    const d = removeTime(dateString);
    date = d ? convertStringToDate(d, FORMAT_ISO_DATE) : new Date(Number.NaN);
  }
  if (!Number.isNaN(date.getTime())) {
    const z = formatInstantForPattern(date, forMatString, tz);
    if (z !== null) return z;
  }
  const d = removeTime(dateString);
  return d
    ? format(convertStringToDate(d, FORMAT_ISO_DATE), forMatString, {
        locale: locale == 'th' ? th : enUS,
      })
    : undefined;
}

/**
 * Compare a QDate-mask-formatted date string against today in the store's timezone.
 * Use with QDate's `:events` prop to render a store-timezone "today" indicator dot:
 *
 *     <q-date :events="(d) => isStoreToday(d, 'MM/DD/YYYY')" mask="MM/DD/YYYY" ... />
 */
export const isStoreToday = (
  dateString: string,
  mask: 'MM/DD/YYYY' | 'YYYY-MM-DD' = 'YYYY-MM-DD',
  timezone?: string,
): boolean => {
  if (!dateString) return false;
  const tz = timezone || getTimezoneFromStorage();
  const todayIso = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  if (mask === 'YYYY-MM-DD') return dateString === todayIso;
  const [y, m, d] = todayIso.split('-');
  return dateString === `${m}/${d}/${y}`;
};

/**
 * QDate `:options` predicate — allow only today/future (store timezone). QDate passes the
 * candidate as `YYYY/MM/DD` regardless of `mask`. Already-bound past values stay valid (only
 * new picks are restricted).
 */
export const isNotPastDate = (dateString: string, timezone?: string): boolean => {
  if (!dateString) return true;
  const tz = timezone || getTimezoneFromStorage();
  const todayIso = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return dateString >= todayIso.replaceAll('-', '/');
};

// ─── `formatDateTime` (legacy, used by useBase + auto-formatters) ──────────

/**
 * @example formatDateTime('2022-05-25 17:26:31', 'dd MMMM yyyy', locale.value)
 */
export const formatDateTime = (
  dateString: string,
  forMatString: string,
  locale: string | unknown,
  timezone?: string,
) => {
  const tz = timezone ?? getTimezoneFromStorage();
  let date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    date = convertStringToDate(dateString);
  }
  if (!Number.isNaN(date.getTime())) {
    const z = formatInstantForPattern(date, forMatString, tz);
    if (z !== null) return z;
  }
  return format(convertStringToDate(dateString), forMatString, {
    locale: locale == 'th' ? th : enUS,
  });
};

// ─── date-fns "from now" wrappers ───────────────────────────────────────────

/** @example formatRelativeFromNow('2022-05-25 17:26:31', locale.value) */
export const formatRelativeFromNow = (
  dateString: string | undefined,
  locale: string = 'th',
) => {
  if (!dateString) return;
  return formatRelative(Date.parse(dateString), new Date(), {
    locale: locale == 'th' ? th : enUS,
  });
};

/** @example formatDistanceFromNow('2022-05-25 17:26:31', locale.value) */
export const formatDistanceFromNow = (
  dateString: string,
  locale: string | unknown,
  suffix: boolean = false,
) => {
  return formatDistanceToNow(convertStringToDate(dateString), {
    locale: locale == 'th' ? th : enUS,
    addSuffix: suffix,
  });
};

// ─── Auto-format helpers (driven by recency, used by useBase wrappers) ─────

export const getDateAutoFormatBy = (
  dateString: string | undefined,
  locale: string = 'th',
) => {
  if (!dateString) return '';
  const difDays = getDateDiffNow(dateString);
  if (difDays > 0 && difDays < 365) {
    return formatDateTime(dateString, FORMAT_DDMM, locale);
  } else if (difDays > 365) {
    return formatDateTime(dateString, FORMAT_SHORT_DDMMYY, locale);
  }
  return formatDateTime(dateString, FORMAT_HHMM, locale);
};

export const getDateTimeAutoFormatBy = (
  dateString: string | undefined,
  locale: string = 'th',
) => {
  if (!dateString) return '';
  const difDays = getDateDiffNow(dateString);
  if (difDays >= 0 && difDays < 1) {
    return formatDateTime(dateString, FORMAT_HHMM, locale);
  } else if (difDays >= 1 && difDays < 365) {
    return formatDateTime(dateString, FORMAT_DDMM_HHMM, locale);
  } else {
    return formatDateTime(dateString, FORMAT_DDMMYYYY_HHMM, locale);
  }
};

// ─── Timezone-scoped time helpers ──────────────────────────────────────────

/** Hour (0–23) and minute for an instant in the given IANA timezone. */
export function getHourAndMinuteInTimezone(
  dateInput: string | Date,
  timezone?: string,
): { hour: number; minute: number } {
  const tz = timezone || getTimezoneFromStorage();
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (Number.isNaN(date.getTime())) return { hour: 0, minute: 0 };
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      timeZone: tz,
    }).formatToParts(date);
    const hour = Number.parseInt(parts.find(p => p.type === 'hour')?.value ?? '0', 10);
    const minute = Number.parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10);
    return { hour, minute };
  } catch {
    return { hour: date.getHours(), minute: date.getMinutes() };
  }
}

/** Time only `HH:mm` in store timezone (or override). */
export const formatTimeWithStoreTimezone = (
  dateString: string | Date | undefined,
  timezone?: string,
): string => {
  if (!dateString) return '';
  try {
    const storeTimezone = timezone || getTimezoneFromStorage();
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: storeTimezone,
      hour12: false,
    }).format(date);
  } catch (error) {
    console.error('Error formatting time with store timezone:', error);
    return formatDateTime(
      typeof dateString === 'string' ? dateString : dateString.toISOString(),
      FORMAT_HHMM,
      'en',
    );
  }
};

/** ISO `yyyy-MM-dd` for an instant in the store timezone (or override). */
export function formatToDateString(
  dateInput: string | Date | number | null | undefined,
  timezone?: string,
): string {
  if (!dateInput) return '';
  try {
    const date = new Date(dateInput);
    const storeTimezone = timezone || getTimezoneFromStorage();
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: storeTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch (err) {
    console.error('Invalid date input:', dateInput, err);
    return '';
  }
}

// ─── Duration / "time ago" helpers ─────────────────────────────────────────

export const formatTimeDifference = (
  startTime: string | Date,
  endTime: Date,
  format: 'elapsed' | 'remaining' = 'elapsed',
): string => {
  try {
    const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
    const diffMs = format === 'elapsed'
      ? endTime.getTime() - start.getTime()
      : start.getTime() - endTime.getTime();
    if (format === 'remaining' && diffMs <= 0) return '0m';
    const absDiffMs = Math.abs(diffMs);
    const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
    const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return format === 'elapsed' ? `${hours}:${minutes}m` : `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return '0m';
  } catch {
    return '0m';
  }
};

/**
 * So sánh một date UTC với thời gian hiện tại và trả về string mô tả.
 * - Nếu > 24h: trả về "X day(s) ago"
 * - Nếu < 24h: dựa trên thời gian (hours, minutes, seconds ago)
 */
export const getTimeAgo = (utcDateString: string | Date): string => {
  try {
    const date = typeof utcDateString === 'string' ? new Date(utcDateString) : utcDateString;
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const hoursDiff = differenceInHours(now, date);
    if (hoursDiff >= 24) {
      const daysDiff = differenceInDays(now, date);
      return daysDiff === 1 ? '1 day ago' : `${daysDiff} days ago`;
    }
    if (hoursDiff > 0) {
      return hoursDiff === 1 ? '1 hour ago' : `${hoursDiff} hours ago`;
    }
    const minutesDiff = differenceInMinutes(now, date);
    if (minutesDiff > 0) {
      return minutesDiff === 1 ? '1 minute ago' : `${minutesDiff} minutes ago`;
    }
    const secondsDiff = differenceInSeconds(now, date);
    if (secondsDiff > 0) {
      return secondsDiff === 1 ? '1 second ago' : `${secondsDiff} seconds ago`;
    }
    return 'Today';
  } catch (error) {
    console.error('Error calculating time ago:', error);
    return '';
  }
};

/** Offset (ms) of `timezone` at `instant` = (wall-clock of instant in tz, read as UTC) − instant. */
const tzOffsetMs = (instant: number, timezone: string): number => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(new Date(instant)).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - instant;
};

/**
 * Convert a wall-clock date+time *in `timezone`* to a UTC ISO instant.
 * @example zonedWallTimeToUtcIso('2026-06-10', '16:20', 'America/Los_Angeles')
 */
export const zonedWallTimeToUtcIso = (
  dateYmd: string,
  timeHm: string | null | undefined,
  timezone?: string,
): string => {
  const tz = timezone || getTimezoneFromStorage();
  const [y, mo, d] = dateYmd.split('-').map(Number);
  const [h, mi] = (timeHm || '00:00').split(':').map(Number);
  if (!y || !mo || !d) return '';
  const wallAsUtc = Date.UTC(y, mo - 1, d, h || 0, mi || 0, 0);
  const offset = tzOffsetMs(wallAsUtc, tz);
  return new Date(wallAsUtc - offset).toISOString();
};

/** Split a UTC ISO instant into its wall-clock `{ date: 'YYYY-MM-DD', time: 'HH:mm' }` in `timezone`. */
export const utcIsoToZonedWall = (
  iso: string | null | undefined,
  timezone?: string,
): { date: string; time: string } => {
  if (!iso) return { date: '', time: '' };
  const tz = timezone || getTimezoneFromStorage();
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return { date: '', time: '' };
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
};
